/**
 * Commit/revert rules for the planner's inline (contenteditable) name editors
 * - day cards and route cards both rename in place rather than through a
 * modal.
 *
 * This lives outside the component because contenteditable hands back whatever
 * the browser put in the node, not what the user meant to type: Chrome inserts
 * non-breaking spaces mid-word, pasted rich text arrives with newlines and
 * indentation, and an emptied field returns "" rather than the original.
 */

export type NameEditAction = 'commit' | 'revert';

export interface NameEditResult {
  action: NameEditAction;
  /** What the element should display once the edit settles. */
  name: string;
}

/** Flatten everything contenteditable can smuggle in down to single spaces. */
export function normalizeEditedName(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return '';
  // JS \s covers the non-breaking spaces Chrome inserts mid-word as well as
  // the newlines and tabs that ride in on pasted markup.
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * Decide what a finished inline edit means.
 *
 * `currentName` is the *effective* name shown before editing began. For a day
 * that was never renamed that is the "Day 3" placeholder, so accepting the
 * placeholder unchanged reverts instead of persisting "Day 3" as a custom
 * name - otherwise a stray double-click would pin a name that then stops
 * following the day if the trip gets re-split.
 */
export function resolveNameEdit(
  raw: string | null | undefined,
  currentName: string | null | undefined,
): NameEditResult {
  const next = normalizeEditedName(raw);
  const current = normalizeEditedName(currentName);

  // Empty means "I changed my mind", not "delete the name" - there is no
  // nameless state to fall back to.
  if (!next || next === current) return { action: 'revert', name: current };

  return { action: 'commit', name: next };
}

export interface DayNameClickState {
  /** The inline editor is open on this day. */
  isEditing: boolean;
  /** This day's card is the one currently expanded in the accordion. */
  isExpanded: boolean;
}

/**
 * Whether a click on a day name should reach the header's accordion toggle.
 *
 * The name sits inside the header, so by default clicking it expands or
 * collapses the card. Two cases have to be held back:
 *
 *  - mid-edit, where the click is only placing the caret;
 *  - on an already-expanded day, because the first click of a
 *    double-click-to-rename would otherwise collapse the card and deactivate
 *    its routes just as the editor opens.
 *
 * The trade-off is that the name text expands the card but never collapses it.
 * The rest of the header and the chevron still do both.
 */
export function shouldBubbleDayNameClick({ isEditing, isExpanded }: DayNameClickState): boolean {
  if (isEditing) return false;
  return !isExpanded;
}

/**
 * Whether a click is the tail of a double-click (`MouseEvent.detail` counts
 * clicks in the current sequence). Those are swallowed so a rename gesture
 * can't toggle the accordion open and shut underneath the editor.
 */
export function isDoubleClickTail(detail: number | undefined): boolean {
  return typeof detail === 'number' && detail > 1;
}
